import React, { useEffect, useState } from "react";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import { RootState } from "../../../../redux/store";
import {
  generateGetURL,
  generateUploadURL,
  uploadMedia,
  verifyMediaUpload,
} from "../../../../services/mediaApiService";
import { updateGiftTable } from "../../../../redux/slices/giftTableSlice";
import { eventTypeLocale } from "../../../../constants/eventTypeLocale";
import { themeLocale } from "../../../../constants/themeLocale";
import { setPhoto, getPhoto } from "../../../../utils/indexedDB";

import placeHolderImage from "../../../../assets/placeholders/picture.jpg";
import editIcon from "../../../../assets/icons/edit.svg";

const LOCALE = "modules_MyGiftTable_components_AsideSection_EventDetails";

interface EventDetailsProps {
  allowEditImage?: boolean;
}

const EventDetails: React.FC<EventDetailsProps> = ({ allowEditImage }) => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useAppDispatch();
  const { eventName, eventDate, eventType, theme, profilePicture } =
    useAppSelector((state: RootState) => state.giftTable);
  const userId = useAppSelector((state: RootState) => state.auth.user?.id);

  const [profileImage, setProfileImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchAndCacheProfileImage = async () => {
      if (!profilePicture) return;

      try {
        // Check if the image is cached
        const cachedPhoto = await getPhoto(profilePicture);
        if (cachedPhoto) {
          setProfileImage(cachedPhoto);
          return;
        }

        // Fetch image from the server
        const media = await generateGetURL(profilePicture);
        const response = await fetch(media.signedURL);

        if (!response.ok) throw new Error("Failed to fetch image");

        const buffer = await response.arrayBuffer();
        const binary = new Uint8Array(buffer);
        let binaryString = "";
        for (let i = 0; i < binary.length; i++) {
          binaryString += String.fromCharCode(binary[i]);
        }
        const base64Image = `data:image/jpeg;base64,${btoa(binaryString)}`;

        // Cache and set the image
        setProfileImage(base64Image);
        await setPhoto(profilePicture, base64Image);
      } catch (error) {
        console.error("Error fetching profile picture:", error);
        setProfileImage(null);
      }
    };

    fetchAndCacheProfileImage();
  }, [profilePicture]);

  const handleEditImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;

    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onloadend = async () => {
      const result = reader.result as string;
      setProfileImage(result);

      // Save the new photo offline
      const fileName = `${new Date()
        .toISOString()
        .replace(/[:.-]/g, "")}-${userId}-gift-table-profile.${file.name
        .split(".")
        .pop()}`;
      await setPhoto(fileName, result);

      // Upload the image to the server
      try {
        const { presignedURL, mediaID } = await generateUploadURL(
          fileName,
          userId!
        );
        await uploadMedia(presignedURL, file);
        await verifyMediaUpload(mediaID);

        // Update state with the new profile picture ID
        dispatch(updateGiftTable({ profilePicture: mediaID }));
      } catch (error) {
        console.error("Failed to upload new profile picture:", error);
      }
    };

    reader.readAsDataURL(file);
  };

  return (
    <div className="bg-secondary p-4 rounded-md space-y-4 relative">
      <div className="relative">
        <img
          src={profileImage || placeHolderImage}
          alt={t("eventImage")}
          className="w-32 h-32 rounded-full object-cover mx-auto mb-4"
        />
        {allowEditImage && (
          <div className="absolute top-2 right-2 p-1 bg-white rounded-full cursor-pointer">
            <label htmlFor="uploadImage" className="cursor-pointer">
              <img src={editIcon} alt={t("editImage")} className="h-5 w-5" />
            </label>
            <input
              id="uploadImage"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleEditImage}
            />
          </div>
        )}
      </div>
      <div>
        <p className="text-sm">{t("eventNameLabel")}</p>
        <h4 className="text-lg font-bold">{eventName || t("notAvailable")}</h4>
      </div>
      <div>
        <p className="text-sm">{t("eventDateLabel")}</p>
        <h4 className="text-lg font-bold">{eventDate || t("notAvailable")}</h4>
      </div>
      <div>
        <p className="text-sm">{t("eventTypeLabel")}</p>
        <h4 className="text-lg font-bold">
          {t(eventTypeLocale[eventType]) || t("notAvailable")}
        </h4>
      </div>
      <div>
        <p className="text-sm">{t("eventThemeLabel")}</p>
        <h4 className="text-lg font-bold">
          {t(themeLocale[theme]) || t("notAvailable")}
        </h4>
      </div>
    </div>
  );
};

export default withTranslations(LOCALE)(EventDetails);
